import React, { useState, useEffect } from 'react';
import { AlertCircle, Sun, Zap, DollarSign, Building2, X, Battery, ArrowLeft, GripVertical, ChevronDown, Check, Target, Fuel, Lightbulb, Factory, Crop, BarChart3 } from 'lucide-react';
import type { BillData, UserType } from '../types';
import { Footer } from './Footer';
import { MonthlyConsumptionInput } from './MonthlyConsumptionInput';

interface EnergyProfileAgriculturalProps {
  onNext: (data: BillData) => void;
  onBack?: () => void;
  userType: UserType | null;
  initialData?: BillData | null;
}

export function EnergyProfileAgricultural({ onNext, onBack, initialData }: EnergyProfileAgriculturalProps) {
  const [billData] = useState<BillData>({
    type: 'bill',
    monthlyUsage: 0,
    monthlyBill: 0,
    utilityProvider: '',
    yearlyConsumption: 0,
    locationPhotos: []
  });

  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // New agricultural-specific fields
  const [farmArea, setFarmArea] = useState<number>(0);
  const [farmLocation, setFarmLocation] = useState<string>('');

  // New state for interactive features
  const availableObjectives = [
    { id: 1, text: 'Reduce electricity costs', icon: DollarSign },
    { id: 2, text: 'Environmental impact', icon: Sun },
    { id: 3, text: 'Energy independence', icon: Battery },
    { id: 4, text: 'Government incentives', icon: Target },
    { id: 5, text: 'Increase property value', icon: Building2 },
    { id: 6, text: 'Backup power supply', icon: Zap },
    { id: 7, text: 'Corporate sustainability goals', icon: Factory },
  ];

  const [selectedObjectives, setSelectedObjectives] = useState<Array<{id: number, text: string, icon: any, rank: number}>>([]);
  const [draggedObjectiveIndex, setDraggedObjectiveIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [objectivesError, setObjectivesError] = useState<string | null>(null);
  const [energyMixError, setEnergyMixError] = useState<string | null>(null);

  const [energyMix, setEnergyMix] = useState<{[key: string]: number}>({});
  const [draggedEnergy, setDraggedEnergy] = useState<string | null>(null);
  
  const energySources = [
    { id: 'electricity', name: 'Electricity', icon: Lightbulb, color: 'bg-yellow-500' },
    { id: 'diesel', name: 'Diesel', icon: Fuel, color: 'bg-red-500' },
    { id: 'natural-gas', name: 'Natural Gas', icon: Factory, color: 'bg-blue-500' },
    { id: 'fuel-oil', name: 'Fuel Oil', icon: Fuel, color: 'bg-orange-500' },
    { id: 'solar', name: 'Solar (Existing)', icon: Sun, color: 'bg-green-500' },
  ];

  // Consumption state
  const [knowsConsumption, setKnowsConsumption] = useState(true);
  const [monthlyConsumptionArray, setMonthlyConsumptionArray] = useState<number[]>(new Array(12).fill(0));
  const [estimatedMonthlyKwh, setEstimatedMonthlyKwh] = useState<number>(0);
  const [consumptionError, setConsumptionError] = useState<string | null>(null);
  const [morningConsumptionPct, setMorningConsumptionPct] = useState<number | undefined>(initialData?.morningConsumptionPct);
  const [morningConsumptionError, setMorningConsumptionError] = useState<string | null>(null);
  const [customTariffSarPerKwh, setCustomTariffSarPerKwh] = useState<number | undefined>(initialData?.customTariffSarPerKwh);
  const [customTariffError, setCustomTariffError] = useState<string | null>(null);

  const [showMoreSources, setShowMoreSources] = useState(false);
  const [customEnergyName, setCustomEnergyName] = useState('');
  const [customEnergySources, setCustomEnergySources] = useState<Array<{id: string, name: string, icon: any, color: string}>>([]);
  const [customEnergyError, setCustomEnergyError] = useState<string>('');

  // Helper function to check if energy source name is unique
  const isEnergySourceNameUnique = (name: string): boolean => {
    const allNames = [
      ...energySources.map(s => s.name.toLowerCase()),
      ...additionalEnergySources.map(s => s.name.toLowerCase()),
      ...customEnergySources.map(s => s.name.toLowerCase()),
      ...Object.keys(energyMix).map(key => {
        const source = allEnergySources.find(s => s.id === key);
        return source ? source.name.toLowerCase() : key.toLowerCase();
      })
    ];
    return !allNames.includes(name.toLowerCase());
  };

  // Helper function to get a unique color for custom energy sources
  const getCustomEnergySourceColor = (): string => {
    const colors = [
      'bg-pink-500', // Pink
      'bg-purple-500', // Purple
      'bg-cyan-500', // Cyan
      'bg-amber-500', // Amber
      'bg-red-500', // Red
      'bg-emerald-500', // Emerald
      'bg-orange-500', // Orange
      'bg-lime-500', // Lime
      'bg-indigo-500', // Indigo
      'bg-rose-500', // Rose
      'bg-teal-500', // Teal
      'bg-fuchsia-500', // Fuchsia
    ];
    
    // Get the number of existing custom sources to determine color index
    const colorIndex = customEnergySources.length % colors.length;
    return colors[colorIndex];
  };
  
  const additionalEnergySources = [
    { id: 'coal', name: 'Coal', icon: Factory, color: 'bg-gray-600' },
    { id: 'wind', name: 'Wind', icon: Sun, color: 'bg-cyan-500' },
    { id: 'hydro', name: 'Hydro', icon: Zap, color: 'bg-blue-600' },
    { id: 'biomass', name: 'Biomass', icon: Factory, color: 'bg-green-600' },
  ];

  // Combined energy sources for pie chart lookup
  const allEnergySources = [...energySources, ...additionalEnergySources, ...customEnergySources];

  // Initialize state from initialData
  useEffect(() => {
    if (initialData) {
      if (initialData.energyMix) {
        setEnergyMix(initialData.energyMix);
      }
      if (initialData.objectives) {
        setSelectedObjectives(initialData.objectives);
      }
      if (initialData.customEnergySources) {
        setCustomEnergySources(initialData.customEnergySources);
      }
      if (initialData.farmArea) {
        setFarmArea(initialData.farmArea);
      }
      if (initialData.farmLocation) {
        setFarmLocation(initialData.farmLocation);
      }
      if (initialData.monthlyConsumptionArray && initialData.monthlyConsumptionArray.length === 12) {
        setMonthlyConsumptionArray(initialData.monthlyConsumptionArray);
        setKnowsConsumption(true);
      } else if (initialData.monthlyUsage && initialData.monthlyUsage > 0) {
        setEstimatedMonthlyKwh(initialData.monthlyUsage);
        setKnowsConsumption(false);
      }
      if (typeof initialData.morningConsumptionPct === 'number' && Number.isFinite(initialData.morningConsumptionPct)) {
        setMorningConsumptionPct(initialData.morningConsumptionPct);
      }
      if (typeof initialData.customTariffSarPerKwh === 'number' && Number.isFinite(initialData.customTariffSarPerKwh)) {
        setCustomTariffSarPerKwh(initialData.customTariffSarPerKwh);
      }
    }
  }, [initialData]);

  const validateConsumption = (): boolean => {
    if (knowsConsumption) {
      const total = monthlyConsumptionArray.reduce((s, v) => s + (Number.isFinite(v) ? v : 0), 0);
      if (total <= 0) {
        setConsumptionError('Please enter your monthly electricity consumption for at least one month');
        return false;
      }
    } else {
      if (estimatedMonthlyKwh <= 0) {
        setConsumptionError('Please enter your estimated monthly consumption');
        return false;
      }
    }
    setConsumptionError(null);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUploading(true);
    setUploadError(null);

    if (farmArea <= 0) {
      setUploadError('Please enter a valid farm area');
      setIsUploading(false);
      return;
    }

    if (!farmLocation.trim()) {
      setUploadError('Please enter your farm location');
      setIsUploading(false);
      return;
    }

    if (!validateObjectives(selectedObjectives)) {
      setIsUploading(false);
      return;
    }

    if (!validateEnergyMix(energyMix)) {
      setIsUploading(false);
      return;
    }

    if (!validateConsumption()) {
      setIsUploading(false);
      return;
    }

    if (
      morningConsumptionPct != null &&
      (!Number.isFinite(morningConsumptionPct) || morningConsumptionPct < 0 || morningConsumptionPct > 100)
    ) {
      setMorningConsumptionError('Morning consumption percentage must be between 0 and 100');
      setIsUploading(false);
      return;
    }
    setMorningConsumptionError(null);

    if (
      customTariffSarPerKwh != null &&
      (!Number.isFinite(customTariffSarPerKwh) || customTariffSarPerKwh <= 0 || customTariffSarPerKwh > 10)
    ) {
      setCustomTariffError('Custom tariff must be between 0 and 10 SAR/kWh');
      setIsUploading(false);
      return;
    }
    setCustomTariffError(null);

    try {
      const finalMonthly = knowsConsumption
        ? monthlyConsumptionArray
        : new Array(12).fill(estimatedMonthlyKwh);

      const yearlyTotal = finalMonthly.reduce((s, v) => s + (Number.isFinite(v) ? v : 0), 0);
      const normalizedMorningPct =
        typeof morningConsumptionPct === 'number' && Number.isFinite(morningConsumptionPct)
          ? Math.max(0, Math.min(100, morningConsumptionPct))
          : 100;
      const factor = normalizedMorningPct / 100;
      const effectiveYearlyConsumption = yearlyTotal * factor;

      onNext({
        ...billData,
        type: 'bill',
        monthlyConsumptionArray: finalMonthly,
        yearlyConsumption: yearlyTotal,
        monthlyUsage: yearlyTotal / 12,
        morningConsumptionPct: normalizedMorningPct,
        customTariffSarPerKwh:
          typeof customTariffSarPerKwh === 'number' && Number.isFinite(customTariffSarPerKwh) && customTariffSarPerKwh > 0
            ? customTariffSarPerKwh
            : undefined,
        effectiveConsumptionFactor: factor,
        effectiveYearlyConsumption,
        energyMix: energyMix,
        energySources: allEnergySources,
        objectives: selectedObjectives,
        customEnergySources: customEnergySources,
        farmArea: farmArea,
        farmLocation: farmLocation
      });

    } catch (error) {
      console.error('Submit error:', error);
      setUploadError((error as Error).message || 'Failed to submit energy profile. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  // Validation function for objectives
  const validateObjectives = (objectives: Array<{id: number, text: string, icon: any, rank: number}>) => {
    if (objectives.length < 3) {
      setObjectivesError(`Please select at least 3 objectives. Currently selected: ${objectives.length}`);
      return false;
    }
    setObjectivesError(null);
    return true;
  };

  // Validation function for energy mix
  const validateEnergyMix = (mix: {[key: string]: number}) => {
    const selectedSources = Object.keys(mix).filter(key => mix[key] > 0);
    const totalPercentage = Object.values(mix).reduce((sum, percentage) => sum + percentage, 0);
    
    if (selectedSources.length === 0) {
      setEnergyMixError('Please select at least one energy source');
      return false;
    }
    
    if (totalPercentage !== 100) {
      setEnergyMixError(`Energy mix percentages must total exactly 100%. Current total: ${totalPercentage.toFixed(1)}%`);
      return false;
    }
    
    setEnergyMixError(null);
    return true;
  };

  // New handler functions for interactive features
  const addObjectiveToRanking = (objective: any) => {
    const newObjective = {
      ...objective,
      rank: selectedObjectives.length + 1
    };
    const updatedObjectives = [...selectedObjectives, newObjective];
    setSelectedObjectives(updatedObjectives);
    validateObjectives(updatedObjectives);
  };

  const removeObjectiveFromRanking = (objectiveId: number) => {
    setSelectedObjectives(prev => {
      const filtered = prev.filter(obj => obj.id !== objectiveId);
      // Re-rank remaining objectives
      const reRanked = filtered.map((obj, index) => ({
        ...obj,
        rank: index + 1
      }));
      validateObjectives(reRanked);
      return reRanked;
    });
  };

  const handleObjectiveDragStart = (index: number) => {
    setDraggedObjectiveIndex(index);
  };

  const handleObjectiveDragEnd = () => {
    setDraggedObjectiveIndex(null);
    setDragOverIndex(null);
  };

  const handleObjectiveDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedObjectiveIndex !== null && draggedObjectiveIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleObjectiveDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedObjectiveIndex !== null && draggedObjectiveIndex !== dropIndex) {
      const draggedObjective = selectedObjectives[draggedObjectiveIndex];
      const newObjectives = [...selectedObjectives];
      newObjectives.splice(draggedObjectiveIndex, 1);
      newObjectives.splice(dropIndex, 0, draggedObjective);
      
      // Update ranks based on new positions
      const rankedObjectives = newObjectives.map((obj, index) => ({
        ...obj,
        rank: index + 1
      }));
      
      setSelectedObjectives(rankedObjectives);
      validateObjectives(rankedObjectives);
    }
    setDraggedObjectiveIndex(null);
    setDragOverIndex(null);
  };

  const handleEnergyDrop = (energyId: string) => {
    if (!energyMix[energyId]) {
      setEnergyMix(prev => {
        const updatedMix = {
        ...prev,
          [energyId]: 0 // Default percentage
        };
        validateEnergyMix(updatedMix);
        return updatedMix;
      });
    }
  };

  const updateEnergyPercentage = (energyId: string, percentage: number) => {
    setEnergyMix(prev => {
      const currentTotal = Object.entries(prev).reduce((sum, [key, val]) => {
        return key === energyId ? sum : sum + val;
      }, 0);
      
      const maxAllowed = 100 - currentTotal;
      const newPercentage = Math.max(0, Math.min(maxAllowed, percentage));
      
      const updatedMix = {
        ...prev,
        [energyId]: newPercentage
      };
      
      validateEnergyMix(updatedMix);
      return updatedMix;
    });
  };

  const removeEnergySource = (energyId: string) => {
    setEnergyMix(prev => {
      const newMix = { ...prev };
      delete newMix[energyId];
      validateEnergyMix(newMix);
      return newMix;
    });
  };

  const getTotalPercentage = () => {
    return Object.values(energyMix).reduce((sum, val) => sum + val, 0);
  };

  const addCustomEnergySource = () => {
    if (customEnergyName.trim()) {
      const trimmedName = customEnergyName.trim();
      
      // Check for uniqueness
      if (!isEnergySourceNameUnique(trimmedName)) {
        setCustomEnergyError('This energy source name is already used. Please choose a different name.');
        return;
      }

      // Clear any previous error
      setCustomEnergyError('');

      // Use the name directly as the key
      const newCustomSource = {
        id: trimmedName, // Use name as ID
        name: trimmedName,
        icon: Zap,
        color: getCustomEnergySourceColor()
      };
      
      // Add to custom sources array
      setCustomEnergySources(prev => [...prev, newCustomSource]);
      
      // Add to energy mix using the name as key
      handleEnergyDrop(trimmedName);
      
      // Clear input
      setCustomEnergyName('');
    }
  };

  // Pie chart helper function
  const createPieChart = () => {
    const total = getTotalPercentage();
    if (total === 0) return null;

    const radius = 80;
    const centerX = 100;
    const centerY = 100;
    let currentAngle = 0;

    return Object.entries(energyMix).map(([energyId, percentage]) => {
      const source = allEnergySources.find(s => s.id === energyId);
      if (!source || percentage === 0) return null;

      const angle = (percentage / total) * 360;
      const startAngle = currentAngle;
      const endAngle = currentAngle + angle;
      
      currentAngle += angle;

      const startAngleRad = (startAngle * Math.PI) / 180;
      const endAngleRad = (endAngle * Math.PI) / 180;

      const largeArcFlag = angle > 180 ? 1 : 0;

      const x1 = centerX + radius * Math.cos(startAngleRad);
      const y1 = centerY + radius * Math.sin(startAngleRad);
      const x2 = centerX + radius * Math.cos(endAngleRad);
      const y2 = centerY + radius * Math.sin(endAngleRad);

      const pathData = [
        `M ${centerX} ${centerY}`,
        `L ${x1} ${y1}`,
        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
        'Z'
      ].join(' ');

      // Convert Tailwind color class to actual color
      const colorMap: {[key: string]: string} = {
        'bg-yellow-500': '#eab308',
        'bg-red-500': '#ef4444',
        'bg-blue-500': '#3b82f6',
        'bg-orange-500': '#f97316',
        'bg-green-500': '#22c55e',
        'bg-gray-600': '#4b5563',
        'bg-cyan-500': '#06b6d4',
        'bg-blue-600': '#2563eb',
        'bg-green-600': '#16a34a',
        'bg-purple-500': '#a855f7',
      };

      return (
        <path
          key={energyId}
          d={pathData}
          fill={colorMap[source.color] || '#6b7280'}
          stroke="white"
          strokeWidth="2"
          className="hover:opacity-80 transition-opacity"
        />
      );
    });
  };

  return (
    <div className="max-w-6xl mx-auto">
      {onBack && (
        <div className="mb-6">
          <button
            onClick={onBack}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back
          </button>
        </div>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="bg-gradient-to-br from-green-500 to-blue-500 rounded-3xl p-8 text-white">
          <h2 className="text-3xl font-bold mb-6">Agricultural Energy Profile</h2>
          <div className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="bg-white/10 p-3 rounded-lg">
                <Crop className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-semibold text-xl">Farm Optimization</h3>
                <p className="text-green-100">Optimize energy for your agricultural operations</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="bg-white/10 p-3 rounded-lg">
                <Battery className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-semibold text-xl">Energy Independence</h3>
                <p className="text-green-100">Reduce dependence on grid electricity</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="bg-white/10 p-3 rounded-lg">
                <DollarSign className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-semibold text-xl">Cost Savings</h3>
                <p className="text-green-100">Significant savings on operational costs</p>
              </div>
            </div>
          </div>
          
          <img
            src="https://i.imgur.com/2agelGC.jpg"
            alt="Agricultural Solar"
            className="mt-8 rounded-xl shadow-lg transform -rotate-2"
          />
        </div>

        <div className="lg:col-span-2 bg-white rounded-3xl shadow-lg p-8 border border-gray-200">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Agricultural-Specific Fields */}
            <div className="border-b pb-6 mb-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <Crop className="h-5 w-5 mr-2 text-green-600" />
                Farm Information
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Tell us about your farm to help us design the perfect energy solution for your agricultural needs.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Farm Area (m²) *
            </label>
            <input
              type="number"
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              value={farmArea}
              onChange={(e) => setFarmArea(Number(e.target.value) || 0)}
              min={0}
              step={1}
              placeholder="Enter farm area in square meters"
              required
            />
            <p className="text-xs text-gray-500">Enter the total area of your farm in square meters (m²)</p>
          </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Farm Location *
                  </label>
                  <input
                    type="text"
                    className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    value={farmLocation}
                    onChange={(e) => setFarmLocation(e.target.value)}
                    placeholder="Enter your farm location"
                    required
                  />
                  <p className="text-xs text-gray-500">City, region, or specific location of your farm</p>
                </div>
              </div>
            </div>

            {/* Objective Ranking Section */}
            <div className="border-b pb-6 mb-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <Target className="h-5 w-5 mr-2 text-blue-600" />
                Installation Objectives
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                First, select your objectives from the available options below. Then drag them to the ranking area to prioritize them.
              </p>
              
              {/* Validation Error Message */}
              {objectivesError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
                  <AlertCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-red-800 font-medium">Objective Selection Required</p>
                    <p className="text-sm text-red-700 mt-1">{objectivesError}</p>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Ranking Area */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-700">Your Priority Ranking</h4>
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                      selectedObjectives.length >= 3 
                        ? 'bg-green-100 text-green-800' 
                        : objectivesError 
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-600'
                    }`}>
                      {selectedObjectives.length}/3 minimum
                    </div>
                  </div>
                  <div 
                    className={`min-h-[200px] border-2 border-dashed rounded-xl p-4 transition-colors ${
                      objectivesError
                        ? 'border-red-300 bg-red-50'
                        : selectedObjectives.length === 0 
                          ? 'border-gray-300 bg-gray-50' 
                          : 'border-blue-300 bg-blue-50'
                    }`}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      // Handle drop from available objectives
                    }}
                  >
                    {selectedObjectives.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full text-gray-500">
                        <Target className="h-12 w-12 mb-3 opacity-50" />
                        <p className="text-center">Drag objectives here to rank them</p>
                        <p className="text-sm text-center mt-1">Most important at the top</p>
                        {objectivesError && (
                          <p className="text-sm text-center mt-2 text-red-600 font-medium">
                            Select at least 3 objectives to continue
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {selectedObjectives.map((objective, index) => {
                          const IconComponent = objective.icon;
                          const isDragging = draggedObjectiveIndex === index;
                          const isDragOver = dragOverIndex === index;
                          
                          return (
                            <div
                              key={objective.id}
                              className={`flex items-center p-3 bg-white border-2 rounded-lg cursor-move transition-all duration-200 ${
                                isDragging 
                                  ? 'border-blue-500 shadow-lg opacity-50 transform rotate-1 scale-105' 
                                  : isDragOver 
                                    ? 'border-green-400 bg-green-50 shadow-md transform scale-102' 
                                    : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                              }`}
                              draggable
                              onDragStart={() => handleObjectiveDragStart(index)}
                              onDragEnd={handleObjectiveDragEnd}
                              onDragOver={(e) => handleObjectiveDragOver(e, index)}
                              onDrop={(e) => handleObjectiveDrop(e, index)}
                            >
                              <GripVertical className={`h-4 w-4 mr-3 transition-colors ${
                                isDragging ? 'text-blue-500' : 'text-gray-400'
                              }`} />
                              <div className={`p-2 rounded-lg mr-3 transition-colors ${
                                isDragging ? 'bg-blue-200' : isDragOver ? 'bg-green-200' : 'bg-blue-100'
                              }`}>
                                <IconComponent className={`h-4 w-4 ${
                                  isDragging ? 'text-blue-700' : isDragOver ? 'text-green-700' : 'text-blue-600'
                                }`} />
                              </div>
                              <span className="flex-1 text-sm font-medium">{objective.text}</span>
                      <button
                        type="button"
                                onClick={() => removeObjectiveFromRanking(objective.id)}
                                className="text-red-500 hover:text-red-700 transition-colors mr-2"
                              >
                                <X className="h-4 w-4" />
                      </button>
                              <div className={`px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                                isDragging ? 'bg-blue-700 text-white' : 
                                isDragOver ? 'bg-green-600 text-white' : 'bg-blue-600 text-white'
                              }`}>
                                #{index + 1}
                    </div>
                </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  </div>

                {/* Available Objectives */}
                  <div>
                  <h4 className="font-medium mb-3 text-gray-700">Available Objectives</h4>
                  <div className="space-y-2">
                    {availableObjectives.map((objective) => {
                      const IconComponent = objective.icon;
                      const isSelected = selectedObjectives.some(selected => selected.id === objective.id);
                      
                      return (
                        <div
                          key={objective.id}
                          className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                            isSelected 
                              ? 'border-gray-200 bg-gray-50 opacity-50' 
                              : 'border-gray-300 hover:border-blue-400 hover:shadow-md'
                          }`}
                          onClick={() => !isSelected && addObjectiveToRanking(objective)}
                        >
                          <div className={`p-2 rounded-lg mr-3 ${isSelected ? 'bg-gray-200' : 'bg-blue-100'}`}>
                            <IconComponent className={`h-4 w-4 ${isSelected ? 'text-gray-500' : 'text-blue-600'}`} />
                          </div>
                          <span className={`flex-1 text-sm font-medium ${isSelected ? 'text-gray-500' : 'text-gray-700'}`}>
                            {objective.text}
                          </span>
                          {isSelected && (
                            <Check className="h-4 w-4 text-green-600" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Interactive Energy Mix Creator */}
            <div className="border-b pb-6 mb-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <Zap className="h-5 w-5 mr-2 text-blue-600" />
                Energy Mix
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Drag energy sources from the left to create your energy mix. Adjust percentages to match your usage.
              </p>
              
              {/* Energy Mix Validation Error Message */}
              {energyMixError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
                  <AlertCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-red-800 font-medium">Energy Mix Configuration Required</p>
                    <p className="text-sm text-red-700 mt-1">{energyMixError}</p>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Available Energy Sources */}
                <div className="lg:col-span-1">
                  <h4 className="font-medium mb-3 text-gray-700">Available Energy Sources</h4>
                  <div className="space-y-2">
                    {energySources.map((source) => {
                      const IconComponent = source.icon;
                      const isUsed = Object.prototype.hasOwnProperty.call(energyMix, source.id);
                      return (
                        <div
                          key={source.id}
                          className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                            isUsed 
                              ? 'border-gray-200 bg-gray-50 opacity-50' 
                              : 'border-gray-300 hover:border-blue-400 hover:shadow-md'
                          }`}
                          draggable={!isUsed}
                          onDragStart={() => !isUsed && setDraggedEnergy(source.id)}
                          onClick={() => !isUsed && handleEnergyDrop(source.id)}
                        >
                          <div className={`p-2 rounded-lg mr-3 ${source.color}`}>
                            <IconComponent className="h-4 w-4 text-white" />
                          </div>
                          <span className="font-medium text-gray-700">{source.name}</span>
                          {isUsed && (
                            <Check className="h-4 w-4 text-green-600 ml-auto" />
                          )}
                        </div>
                      );
                    })}
                    
                    {/* More Sources Button */}
                    <button
                      type="button"
                      onClick={() => setShowMoreSources(!showMoreSources)}
                      className="w-full flex items-center justify-center p-3 rounded-lg border-2 border-dashed border-gray-300 hover:border-blue-400 transition-all text-gray-600 hover:text-blue-600"
                    >
                      <span className="font-medium">More Sources</span>
                      <ChevronDown className={`h-4 w-4 ml-2 transition-transform ${showMoreSources ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {/* Additional Sources */}
                    {showMoreSources && (
                      <div className="space-y-2 mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <h5 className="text-sm font-medium text-gray-700 mb-2">Additional Energy Sources</h5>
                        {additionalEnergySources.map((source) => {
                          const IconComponent = source.icon;
                          const isUsed = Object.prototype.hasOwnProperty.call(energyMix, source.id);
                          return (
                            <div
                              key={source.id}
                              className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                isUsed 
                                  ? 'border-gray-200 bg-gray-100 opacity-50' 
                                  : 'border-gray-300 hover:border-blue-400 hover:shadow-md bg-white'
                              }`}
                              draggable={!isUsed}
                              onDragStart={() => !isUsed && setDraggedEnergy(source.id)}
                              onClick={() => !isUsed && handleEnergyDrop(source.id)}
                            >
                              <div className={`p-2 rounded-lg mr-3 ${source.color}`}>
                                <IconComponent className="h-4 w-4 text-white" />
                              </div>
                              <span className="font-medium text-gray-700">{source.name}</span>
                              {isUsed && (
                                <Check className="h-4 w-4 text-green-600 ml-auto" />
                              )}
                </div>
                          );
                        })}
                        
                        {/* Custom Energy Sources */}
                        {customEnergySources.map((source) => {
                          const IconComponent = source.icon;
                          const isUsed = Object.prototype.hasOwnProperty.call(energyMix, source.id);
                          return (
                            <div
                              key={source.id}
                              className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                isUsed 
                                  ? 'border-gray-200 bg-gray-100 opacity-50' 
                                  : 'border-gray-300 hover:border-blue-400 hover:shadow-md bg-white'
                              }`}
                              draggable={!isUsed}
                              onDragStart={() => !isUsed && setDraggedEnergy(source.id)}
                              onClick={() => !isUsed && handleEnergyDrop(source.id)}
                            >
                              <div className={`p-2 rounded-lg mr-3 ${source.color}`}>
                                <IconComponent className="h-4 w-4 text-white" />
                              </div>
                              <span className="font-medium text-gray-700">{source.name}</span>
                              {isUsed && (
                                <Check className="h-4 w-4 text-green-600 ml-auto" />
                              )}
                      </div>
                          );
                        })}
                        
                        {/* Custom Energy Source Input */}
                        <div className="mt-3 p-3 border-2 border-dashed border-gray-300 rounded-lg bg-white">
                          <p className="text-xs text-gray-600 mb-2">Add custom energy source:</p>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              placeholder="e.g., Geothermal, Nuclear..."
                              value={customEnergyName}
                              onChange={(e) => {
                                setCustomEnergyName(e.target.value);
                                setCustomEnergyError(''); // Clear error when typing
                              }}
                              className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              onKeyPress={(e) => e.key === 'Enter' && addCustomEnergySource()}
                            />
                      <button
                        type="button"
                              onClick={addCustomEnergySource}
                              disabled={!customEnergyName.trim()}
                              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex-shrink-0"
                            >
                              Add
                      </button>
                          </div>
                          {customEnergyError && (
                            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-600 text-xs">
                              {customEnergyError}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Energy Mix Display */}
                <div className="lg:col-span-2">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-700">Your Energy Mix</h4>
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                      Object.keys(energyMix).length > 0 && getTotalPercentage() === 100
                        ? 'bg-green-100 text-green-800' 
                        : energyMixError 
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-600'
                    }`}>
                      {getTotalPercentage().toFixed(1)}% / 100%
                    </div>
                  </div>
                  
                  {Object.keys(energyMix).length === 0 ? (
                    <div 
                      className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                        energyMixError
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-300 bg-gray-50'
                      }`}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (draggedEnergy) {
                          handleEnergyDrop(draggedEnergy);
                          setDraggedEnergy(null);
                        }
                      }}
                    >
                      <Zap className={`mx-auto h-12 w-12 mb-3 ${
                        energyMixError ? 'text-red-400' : 'text-gray-400'
                      }`} />
                      <p className={`${
                        energyMixError ? 'text-red-600' : 'text-gray-500'
                      }`}>
                        Drag energy sources here or click them to add to your mix
                      </p>
                      {energyMixError && (
                        <p className="text-sm text-red-600 mt-2 font-medium">
                          Select at least one energy source to continue
                        </p>
                      )}
                </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Pie Chart */}
                      <div className="flex justify-center">
                        <div className="bg-white border border-gray-200 rounded-xl p-6">
                          <h5 className="text-center font-medium mb-4 text-gray-700">Energy Distribution</h5>
                          <svg width="200" height="200" className="mx-auto">
                            {createPieChart()}
                          </svg>
                          
                          {/* Legend */}
                          <div className="mt-4 space-y-2">
                            {Object.entries(energyMix).map(([energyId, percentage]) => {
                              const source = allEnergySources.find(s => s.id === energyId);
                              if (!source) return null;
                              
                              const colorMap: {[key: string]: string} = {
                                'bg-yellow-500': '#eab308',
                                'bg-red-500': '#ef4444',
                                'bg-blue-500': '#3b82f6',
                                'bg-orange-500': '#f97316',
                                'bg-green-500': '#22c55e',
                                'bg-gray-600': '#4b5563',
                                'bg-cyan-500': '#06b6d4',
                                'bg-blue-600': '#2563eb',
                                'bg-green-600': '#16a34a',
                                'bg-purple-500': '#a855f7',
                              };
                              
                              return (
                                <div key={energyId} className="flex items-center justify-between text-sm">
                      <div className="flex items-center">
                                    <div 
                                      className="w-3 h-3 rounded-full mr-2"
                                      style={{ backgroundColor: colorMap[source.color] || '#6b7280' }}
                                    />
                                    <span>{source.name}</span>
                                  </div>
                                  <span className="font-medium">{percentage.toFixed(1)}%</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>

                      {/* Energy Mix Items */}
                      <div className="space-y-3">
                        {Object.entries(energyMix).map(([energyId, percentage]) => {
                          const source = allEnergySources.find(s => s.id === energyId);
                          if (!source) return null;
                          const IconComponent = source.icon;
                          
                          return (
                            <div key={energyId} className="bg-white border border-gray-200 rounded-lg p-4">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center">
                                  <div className={`p-2 rounded-lg mr-3 ${source.color}`}>
                                    <IconComponent className="h-4 w-4 text-white" />
                                  </div>
                                  <span className="font-medium">{source.name}</span>
                      </div>
                      <button
                        type="button"
                                  onClick={() => removeEnergySource(energyId)}
                        className="text-red-500 hover:text-red-700 transition-colors"
                      >
                                  <X className="h-4 w-4" />
                      </button>
                              </div>
                              
                              <div className="flex items-center space-x-3">
                                <input
                                  type="range"
                                  min="0"
                                  max="100"
                                  value={percentage}
                                  onChange={(e) => updateEnergyPercentage(energyId, Number(e.target.value))}
                                  className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                />
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  value={percentage}
                                  onChange={(e) => updateEnergyPercentage(energyId, Number(e.target.value))}
                                  className="w-16 px-2 py-1 border border-gray-300 rounded text-center text-sm"
                                />
                                <span className="text-sm text-gray-600">%</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Total Percentage Indicator */}
                      <div className={`p-3 rounded-lg text-center font-medium ${
                        energyMixError
                          ? 'bg-red-100 text-red-800'
                          : getTotalPercentage() === 100 
                            ? 'bg-green-100 text-green-800' 
                            : getTotalPercentage() > 100 
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        Total: {getTotalPercentage()}%
                        {getTotalPercentage() !== 100 && (
                          <span className="text-sm ml-2">
                            ({getTotalPercentage() > 100 ? 'Over 100%' : 'Incomplete'})
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Energy Consumption Section */}
            <div className="border-b pb-6 mb-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <BarChart3 className="h-5 w-5 mr-2 text-green-600" />
                Electricity Consumption
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Provide your electricity consumption so we can size your solar system accurately.
              </p>

              {consumptionError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
                  <AlertCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-red-800 font-medium">Consumption Required</p>
                    <p className="text-sm text-red-700 mt-1">{consumptionError}</p>
                  </div>
                </div>
              )}

              <div className="flex gap-3 mb-6">
                <button
                  type="button"
                  onClick={() => setKnowsConsumption(true)}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 text-sm font-medium transition-all ${
                    knowsConsumption
                      ? 'border-green-500 bg-green-50 text-green-800'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                  }`}
                >
                  I know my monthly consumption
                </button>
                <button
                  type="button"
                  onClick={() => setKnowsConsumption(false)}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 text-sm font-medium transition-all ${
                    !knowsConsumption
                      ? 'border-green-500 bg-green-50 text-green-800'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                  }`}
                >
                  I have an estimate
                </button>
              </div>

              {knowsConsumption ? (
                <MonthlyConsumptionInput
                  initialValues={monthlyConsumptionArray}
                  onValuesChange={(values) => {
                    setMonthlyConsumptionArray(values);
                    setConsumptionError(null);
                  }}
                />
              ) : (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Estimated Monthly Electricity Consumption (kWh) *
                    </label>
                    <input
                      type="number"
                      className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      value={estimatedMonthlyKwh || ''}
                      onChange={(e) => {
                        setEstimatedMonthlyKwh(Number(e.target.value) || 0);
                        setConsumptionError(null);
                      }}
                      min={0}
                      placeholder="e.g. 5000"
                    />
                    <p className="text-xs text-gray-500">
                      This value will be used for every month to estimate your yearly consumption.
                    </p>
                  </div>
                  {estimatedMonthlyKwh > 0 && (
                    <div className="p-3 bg-green-50 rounded-lg text-sm text-green-800">
                      Estimated yearly consumption: <span className="font-semibold">{(estimatedMonthlyKwh * 12).toLocaleString()} kWh</span>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Morning Consumption Share (%) <span className="text-gray-500 font-normal">(Optional)</span>
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  className={`block w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                    morningConsumptionError
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:ring-green-500 focus:border-green-500'
                  }`}
                  value={morningConsumptionPct ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setMorningConsumptionPct(raw === '' ? undefined : Number(raw));
                    if (morningConsumptionError) setMorningConsumptionError(null);
                  }}
                  placeholder="Leave empty to use 100%"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Solar sizing will use this share of annual consumption. Empty means 100%.
                </p>
                {morningConsumptionError && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {morningConsumptionError}
                  </p>
                )}
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Tariff (SAR/kWh) <span className="text-gray-500 font-normal">(Optional override)</span>
                </label>
                <input
                  type="number"
                  min="0"
                  max="10"
                  step="0.0001"
                  className={`block w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                    customTariffError
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:ring-green-500 focus:border-green-500'
                  }`}
                  value={customTariffSarPerKwh ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setCustomTariffSarPerKwh(raw === '' ? undefined : Number(raw));
                    if (customTariffError) setCustomTariffError(null);
                  }}
                  placeholder="Example: 0.1800"
                />
                <p className="text-xs text-gray-500 mt-1">
                  If provided, this tariff overrides SEC/default tariff assumptions for all financial calculations.
                </p>
                {customTariffError && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {customTariffError}
                  </p>
                )}
              </div>
            </div>

            {uploadError && (
              <div className="bg-red-50 text-red-700 p-4 rounded-lg">
                <p className="flex items-center">
                  <AlertCircle className="h-5 w-5 mr-2" />
                  {uploadError}
                </p>
              </div>
            )}

            <div className="bg-green-50 p-4 rounded-xl">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">
                    Why do we need this?
                  </h3>
                  <div className="mt-2 text-sm text-green-700">
                    <p>
                      Your agricultural energy profile helps us design the most efficient solar system for your farm operations. We'll analyze your farm size, location, objectives, and energy mix to create a customized solution that maximizes your agricultural productivity and energy savings.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={isUploading}
              className={`w-full bg-gradient-to-r from-green-600 to-blue-600 text-white py-3 px-4 rounded-xl hover:from-green-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transform transition-all duration-150 hover:scale-[1.02] active:scale-[0.98] font-medium text-lg shadow-lg ${
                isUploading ? 'opacity-75 cursor-not-allowed' : ''
              }`}
            >
              {isUploading ? 'Saving Agricultural Energy Profile...' : 'Continue'}
            </button>
          </form>
        </div>
      </div>
      
      <div className="mt-12">
        <Footer />
      </div>
    </div>
  );
}
